import {AutoInitializing, UnLitElement} from "../../../common/elements";
import {customElement, query, queryAll} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {ManagingResources} from "../../../common/lifetime";
import {Chapters} from "./chapters";
import {resolve} from "../../../container";
import {CHAPTER_NAVIGATION_PRIORITY, StickyElements, StickyPriorityElement} from "../sticky/stickyElements";
import {forAllEntries, IntersectionObserverFactory} from "../../../common/observation";
import {ElementFeatures} from "../elementFeatures";
import {ScrollableBar} from "../scrollableBar";
import {Dictionary} from "../dictionary";

@customElement("eop-chapter-navigation")
export class EopChapterNavigation extends ManagingResources(AutoInitializing(UnLitElement)) {

    @query(".chapter-navigation-container")
    private chapterNavigationContainer: HTMLElement;
    @query(".chapter-navigation")
    private chapterNavigation: HTMLElement;

    @query(".items")
    private scrollableArea: HTMLElement;
    @queryAll(".item")
    private navigationItems: NodeListOf<HTMLElement>;

    private stickySentinelIntersectionObserver: IntersectionObserver;
    private scrollBar: ScrollableBar;
    protected dictionary: Dictionary;

    public constructor(
        private chapters: Chapters = resolve(Chapters),
        private stickyElements: StickyElements = resolve(StickyElements),
        private intersectionObserverFactory: IntersectionObserverFactory = resolve(IntersectionObserverFactory)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);
        this.chapters.onChapterChange(() => this.updateActiveChapter(), this);
        this.stickySentinelIntersectionObserver = this.intersectionObserverFactory.create(forAllEntries(entry => {
            if (entry.isIntersecting) {
                this.classList.remove("sticky-mode");
            } else {
                this.classList.add("sticky-mode");
            }
        }), undefined, "sentinel");
    }

    public render(): TemplateResult {
        return html`
            <div class="sticky-sentinel"></div>
            <div class="background-container"></div>
            <div class="chapter-navigation-container">
                <nav class="chapter-navigation" aria-label=${this.dictionary.translate("MSG_CHAPTER_NAVIGATION")}>
                    <ul class="items horizontally-scrollable-area">
                        ${(this.renderChapters())}
                    </ul>
                </nav>
            </div>`;
    }

    private renderChapters(): TemplateResult[] {
        return this.chapters.getChapterData().map((chapter) => html`
            <li class="item scrollable-item">
                <eop-chapter-navigation-item
                        ?active=${chapter.active}
                        .item=${chapter}
                ></eop-chapter-navigation-item>
            </li>`
        );
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);
        this.scrollBar = new ScrollableBar(this.chapterNavigation, this.dictionary);

        this.stickyElements.register(new StickyPriorityElement(this.chapterNavigationContainer)
            .withName("chapter-navigation")
            .withPriority(CHAPTER_NAVIGATION_PRIORITY)
            .withStickyClass("sticky")
            .withScrollSectionsUnderMidpointOfElement());

        this.stickySentinelIntersectionObserver.observe(this.renderRoot.querySelector(".sticky-sentinel")!);

        ElementFeatures.afterContentLoaded(() => this.scrollBar.scrollToItem(0));
    }

    public disconnectedCallback(): void {
        this.stickyElements.unregister(this.chapterNavigationContainer);
        this.stickySentinelIntersectionObserver.disconnect();
        this.scrollBar.disconnect();
        super.disconnectedCallback();
    }

    private updateActiveChapter(): void {
        this.scrollToActiveChapter();
        this.requestUpdate();
    }

    private scrollToActiveChapter(): void {
        const activeChapterIndex = this.chapters.activeChapterIndex();
        if (this.scrollableArea && this.navigationItems.length > 0 && activeChapterIndex !== undefined && activeChapterIndex > -1) {
            if (this.scrollableArea.clientWidth < this.scrollableArea.scrollWidth) {
                this.scrollBar.scrollToItem(activeChapterIndex);
            }
        }
    }
}
